import React, { useEffect, useState } from "react";
import { subscription } from "../lib/api";
import { Sparkles, ArrowRight } from "lucide-react";
import PaywallModal from "./PaywallModal";

/**
 * Shows an in-dashboard banner while the user is on a Pro trial.
 * - Compact pill on Home (compact=true)
 * - Full card on Profile (compact=false)
 * Hidden when the user is not on a trial (i.e. paid, gifted, or free-after-trial).
 */
export default function TrialBanner({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    subscription.status().then(setStatus).catch(() => {});
  }, []);

  if (!status || !status.is_trial) return null;
  const days = status.trial_days_remaining ?? 0;
  const dayLabel = days === 1 ? "1 day" : `${days} days`;

  if (compact) {
    return (
      <>
        <button
          data-testid="trial-banner-compact"
          onClick={() => setPaywall(true)}
          className="w-full flex items-center justify-between gap-3 rounded-2xl bg-[#F5EBD9] border border-[#D6C7A1]/60 px-4 py-3 text-left transition hover:bg-[#efe1c8]"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <Sparkles size={14} className="text-[#8C7A4F] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#8C7A4F] truncate">
                Pro trial · {dayLabel} left
              </div>
              <div className="text-xs text-[#8C7A4F]/80 truncate">
                Continue for $3.99/month — cancel anytime
              </div>
            </div>
          </div>
          <ArrowRight size={14} className="text-[#8C7A4F] shrink-0" />
        </button>
        <PaywallModal open={paywall} onClose={() => setPaywall(false)} />
      </>
    );
  }

  return (
    <>
      <div
        data-testid="trial-banner-full"
        className="rounded-3xl border border-[#D6C7A1]/60 bg-[#F5EBD9]/70 p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up"
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-[#8C7A4F]" />
          <span className="text-xs uppercase tracking-[0.16em] font-semibold text-[#8C7A4F]">
            Pro trial
          </span>
        </div>
        <div className="font-display text-2xl text-[#2E2E2E] mb-1">
          {dayLabel} left in your free trial
        </div>
        <p className="text-[15px] text-[#2E2E2E] leading-relaxed mb-4">
          You're on the full Pro experience — unlimited guidance, coach, and analyzer.
          When your trial ends, keep it for $3.99/month or continue on the free plan.
        </p>
        <button
          data-testid="trial-banner-upgrade"
          onClick={() => setPaywall(true)}
          className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3 text-sm font-medium transition shadow-[0_4px_14px_rgba(79,111,82,0.18)] inline-flex items-center gap-2"
        >
          Continue with LifeGuide Pro — $3.99/month <ArrowRight size={14} />
        </button>
      </div>
      <PaywallModal open={paywall} onClose={() => setPaywall(false)} />
    </>
  );
}
